/**
 * Segnali del menu del Magazzino (15 sez. 3.1 punto 3): numero dei conflitti e pallino della Panoramica,
 * da commerce_stores.sync_state gia' in realtime (nessuna query a ogni render). E contesto del menu per
 * buildResolvedGroups / getEditableLayout di warehouseMenuConfig.ts.
 * Per chi non ha negozi o non ha il permesso: nessun canale aperto, nessun segnale.
 */
import { useMemo } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { useSettingsStore } from '../stores/settingsStore';
import { useCommerceStores } from '../hooks/useCommerceStores';
import { COMMERCE_UI_PHASE } from './phase';
import { menuSignals, type MenuSignals } from './overview';
import { useCommercePresence } from './useCommercePresence';
import { sidebarMenuCtx, type MenuCtx } from './menuRules';

export function useCommerceMenuContext(): MenuCtx & { loading: boolean; menuEnabled: boolean } {
  const presence = useCommercePresence();
  const { hasPermission } = usePermissions();
  const canViewCommerce = hasPermission('settings_ecommerce', 'view');
  // Sezione E-commerce accesa da Impostazioni > Voci menu anche senza negozi (warehouseMenuConfig.commerceEnabled)
  const menuEnabled = useSettingsStore((s) => (s.settings?.warehouse_menu_config as { commerceEnabled?: boolean } | undefined)?.commerceEnabled === true);
  return {
    menuEnabled,
    hasCommerce: presence.hasCommerce,
    hasLegacyWoo: presence.hasLegacyWoo,
    hasWooStore: presence.byProvider.woocommerce > 0,
    canViewCommerce,
    phase: COMMERCE_UI_PHASE,
    loading: presence.loading,
  };
}

export function useCommerceMenuSignals(ctx: Pick<MenuCtx, 'hasCommerce' | 'canViewCommerce'>): MenuSignals {
  const enabled = ctx.hasCommerce && ctx.canViewCommerce;
  const { stores } = useCommerceStores(enabled);
  return useMemo(() => (enabled ? menuSignals(stores) : { conflicts: 0, attention: 'none' as const }), [enabled, stores]);
}

/**
 * Contesto e segnali per la sidebar e l'editor del menu del Magazzino. Senza negozi (motore spento) il
 * contesto e' DEFAULT_MENU_CTX: menu identico a prima, nessun canale realtime aperto.
 */
export function useWarehouseMenuCommerce(): { ctx: MenuCtx; signals: MenuSignals } {
  const raw = useCommerceMenuContext();
  const ctx = useMemo(() => sidebarMenuCtx(raw), [raw.loading, raw.hasCommerce, raw.menuEnabled, raw.hasLegacyWoo, raw.hasWooStore, raw.canViewCommerce, raw.phase]); // eslint-disable-line react-hooks/exhaustive-deps
  const signals = useCommerceMenuSignals(ctx);
  return { ctx, signals };
}
